
import { useRef, useState, useEffect } from 'react'
import { WebAudioPlayer } from '../utils/WebAudioPlayer';
import { useFrame } from '@react-three/fiber'
import { Html } from '@react-three/drei'
import { track } from '@vercel/analytics';
import { useLanguage } from '../i18n/useLanguage.jsx';


// Show the UI again if the user resizes the window (e.g., rotates device or changes screen size)
// (must be after setUiVisible is defined)

export default function ChargeBar({ onTargetHit, disabled = false, onFloatRotation, resetCounter = 0, onChargeRelease, markerSpeed = 0.7, onFailCharge }) {
  // Language hook for translations
  const { t } = useLanguage();
  
  // Audio ref for engine charge sound
  const engineAudioRef = useRef(null);
  const webAudioPlayerRef = useRef(null);
  if (!webAudioPlayerRef.current) webAudioPlayerRef.current = new WebAudioPlayer();
  // Track a key to force re-mount on resize/orientation, and orientation state
  // Only track resizeKey for remounting on resize/orientation
  const [resizeKey, setResizeKey] = useState(0);
  useEffect(() => {
    const handleRefresh = () => setResizeKey(k => k + 1);
    window.addEventListener('resize', handleRefresh);
    window.addEventListener('orientationchange', handleRefresh);
    return () => {
      window.removeEventListener('resize', handleRefresh);
      window.removeEventListener('orientationchange', handleRefresh);
    };
  }, []);
  // Track last input type to prevent double-firing on touch devices
  const lastInputType = useRef(null);
  // Detect if running on a mobile device
  const isMobile = typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent);
  // --- Event Handlers for Mouse/Touch ---
  // Shared logic for starting charge
  const startCharging = () => {
    if (buttonDisabled || disabled) return;
    isChargingRef.current = true;
    setIsCharging(true);
    chargeLevelRef.current = 0;
    if (progressRef.current) {
      progressRef.current.style.width = '0%';
    }
    // Play engine charge sound
    if (!engineAudioRef.current) {
      webAudioPlayerRef.current.loadBuffer('/audio/sfx/Engine_Charge_01.mp3');
      engineAudioRef.current = { source: null, gain: null };
    }
    // Stop any previous
    if (engineAudioRef.current.source) {
      try { engineAudioRef.current.source.stop(); } catch (e) {}
    }
    webAudioPlayerRef.current.play('/audio/sfx/Engine_Charge_01.mp3', { volume: 0.5, loop: true }).then(({ source, gain }) => {
      engineAudioRef.current = { source, gain };
    });
  };

  const handleMouseDown = (e) => {
  // ...removed debug log...
    // Prevent mouse event if touch event just fired
    if (lastInputType.current === 'touch') return;
    lastInputType.current = 'mouse';
    startCharging();
  };

  // Shared logic for stopping charge
  const stopCharging = () => {
    isChargingRef.current = false;
    setIsCharging(false);
    setUiVisible(false); // Always hide the UI after charging ends
    // Stop engine charge sound
    if (engineAudioRef.current && engineAudioRef.current.source) {
      try { engineAudioRef.current.source.stop(); } catch (e) {}
      engineAudioRef.current = { source: null, gain: null };
    }
    // Use existing variables for hit/miss logic and sound
    const margin = 1.5; // percent, to be forgiving at the edges
    const chargePercentage = chargeLevelRef.current * 100;
    const barWidth = 300;
    const markerWidth = 36;
    const markerWidthPercent = (markerWidth / barWidth) * 100;
    const markerStart = markerPosition - markerWidthPercent / 2;
    const markerEnd = markerPosition + markerWidthPercent / 2;
    const willHit = chargePercentage >= markerStart - margin && chargePercentage <= markerEnd + margin;
    // Play blast sound based on hit or miss
  const blastUrl = `/audio/sfx/Engine_Blast_${willHit ? '01' : '02'}.mp3`;
  webAudioPlayerRef.current.play(blastUrl, { volume: 0.5 });
    if (willHit) {
      setMarkerVisible(false);
      if (onTargetHit) {
        onTargetHit();
      }
    } else {
      if (onFloatRotation) {
        onFloatRotation({ rotationIntensity: 2, fireDirection: true });
      }
      setButtonDisabled(true);
      if (onFailCharge) onFailCharge();
    }
  };

  const handleMouseUp = (e) => {
    if (lastInputType.current === 'touch') return;
    lastInputType.current = 'mouse';
    stopCharging();
    if (onChargeRelease) onChargeRelease();
  };

  const handleMouseLeave = (e) => {
    if (lastInputType.current === 'touch') return;
    lastInputType.current = 'mouse';
    isChargingRef.current = false;
    setIsCharging(false);
    // Stop engine charge sound
    if (engineAudioRef.current && engineAudioRef.current.source) {
      try { engineAudioRef.current.source.stop(); } catch (e) {}
      engineAudioRef.current = { source: null, gain: null };
    }
    if (chargeLevelRef.current > 0) {
      setButtonDisabled(true);
    }
  };
  // Touch event handlers set lastInputType to 'touch' to prevent mouse fallback
  const handleTouchStart = (e) => {
  // ...removed debug log...
    lastInputType.current = 'touch';
    startCharging();
  };
  const handleTouchEnd = (e) => {
    lastInputType.current = 'touch';
    stopCharging();
    // Stop engine charge sound
    if (engineAudioRef.current && engineAudioRef.current.source) {
      try { engineAudioRef.current.source.stop(); } catch (e) {}
      engineAudioRef.current = { source: null, gain: null };
    }
    if (onChargeRelease) onChargeRelease();
  };
  const handleTouchCancel = (e) => {
    lastInputType.current = 'touch';
    handleMouseLeave(e);
    // Stop engine charge sound
    if (engineAudioRef.current) {
      engineAudioRef.current.pause();
      engineAudioRef.current.currentTime = 0;
    }
  };
  const progressRef = useRef()
  const isChargingRef = useRef(false) // Immediate ref for charging state
  const chargeLevelRef = useRef(0) // Use ref instead of state for charge level
  const animationStartTimeRef = useRef(0) // Use ref instead of state
  const [markerPosition, setMarkerPosition] = useState(0)
  const [isCharging, setIsCharging] = useState(false)
  const [markerVisible, setMarkerVisible] = useState(true)
  const [uiVisible, setUiVisible] = useState(true)
  const [buttonDisabled, setButtonDisabled] = useState(false)

  // Set random marker position on component mount (page load) and on reset
  useEffect(() => {
    setMarkerPosition(25 + Math.random() * 50) // Random between 25% and 75%
    setMarkerVisible(true)
    setUiVisible(true)
    setButtonDisabled(false)
    isChargingRef.current = false
    setIsCharging(false)
    chargeLevelRef.current = 0
    animationStartTimeRef.current = 0
    if (progressRef.current) progressRef.current.style.width = '0%'
  }, [resetCounter])

  useFrame((state, delta) => {
    if (progressRef.current) {
      if (isChargingRef.current) {
        // Set animation start time when charging begins
        if (animationStartTimeRef.current === 0) {
          animationStartTimeRef.current = state.clock.elapsedTime
          chargeLevelRef.current = 0
          // Immediately update DOM to prevent single-frame flash
          progressRef.current.style.width = '0%'
        }

        // Animate marker position left and right with a sine wave
  const markerOscSpeed = markerSpeed; // seconds for a full oscillation (now controlled by prop)
        const markerOscRange = 50; // percent range (centered at 50%)
        const markerOscMin = 10; // minimum percent
        const markerOscMax = 90; // maximum percent
        const markerTimeElapsed = state.clock.elapsedTime - animationStartTimeRef.current;
        // Sine wave between markerOscMin and markerOscMax
        const markerOsc = Math.sin(markerTimeElapsed * (2 * Math.PI / markerOscSpeed));
        const markerPos = ((markerOsc + 1) / 2) * (markerOscMax - markerOscMin) + markerOscMin;
        setMarkerPosition(markerPos);

        // Create a looping fill animation that goes 0 -> 1 -> 0 -> 1...
        const cycleDuration = 2 // 2 seconds for a full cycle (fill + empty)
        // Use cosine shifted to start at 0: (1 - cos(x)) / 2 gives us 0 -> 1 -> 0 pattern
        const timeElapsed = state.clock.elapsedTime - animationStartTimeRef.current
        const progress = (1 - Math.cos(timeElapsed * (Math.PI / cycleDuration))) / 2
        chargeLevelRef.current = progress
        // Update the fill width based on charge level
        progressRef.current.style.width = `${progress * 100}%`

        // Proximity to marker center (0 = far, 1 = perfect center)
        if (onFloatRotation) {
          const chargePercentage = chargeLevelRef.current * 100
          const markerCenter = markerPos
          // Normalized distance: 0 at center, 1 at 0% or 100%
          let normalizedDistance = Math.abs(chargePercentage - markerCenter) / Math.max(markerCenter, 100 - markerCenter)
          normalizedDistance = Math.min(normalizedDistance, 1)
          if (chargeLevelRef.current === 0) normalizedDistance = 1
          if (chargeLevelRef.current >= 1) normalizedDistance = 1
          const rotationIntensity = 2 * normalizedDistance
          onFloatRotation({ rotationIntensity })
        }
      } else {
        // Reset animation start time when not charging
        if (animationStartTimeRef.current !== 0) {
          animationStartTimeRef.current = 0;
        }
        // Empty the bar when not charging (empties quickly)
        const newLevel = Math.max(chargeLevelRef.current - delta * 2, 0)
        chargeLevelRef.current = newLevel
        // Update the fill width based on charge level
        progressRef.current.style.width = `${newLevel * 100}%`
        // Re-enable button when charge level reaches zero
        if (newLevel === 0 && buttonDisabled) {
          setButtonDisabled(false)
        }
        if (onFloatRotation) onFloatRotation({ proximity: 0, overshoot: 0 })
      }
    }
  })

  return (
    <>
      {uiVisible && !disabled && (
        <Html
          key={resizeKey}
          position={[0, 0, 0]}
          className="chargebar-ui-html"
        >
          <div className={`chargebar-overlay${isMobile ? ' mobile' : ''}`}>
            <div className={`chargebar-row${isMobile ? ' mobile' : ''}`}>
              {/* Charge Bar */}
              <div className={`chargebar-bar${isMobile ? ' mobile' : ''}`}>
                {markerVisible && (
                  <div
                    className="chargebar-marker"
                    style={{ left: `${markerPosition - 5}%` }}
                  />
                )}
                <div
                  ref={progressRef}
                  className="chargebar-fill"
                />
              </div>
              
              {/* CHARGE Button to the right of the bar */}
              <button
                className={`chargebar-btn${buttonDisabled ? ' disabled' : ''}${isMobile ? '' : ' desktop'}`}
                disabled={buttonDisabled}
                onMouseDown={() => { handleMouseDown(); track('CHARGE_BUTTON_HELD'); }}
                onMouseUp={() => { handleMouseUp(); track('CHARGE_BUTTON_RELEASED'); }}
                onTouchStart={() => { handleTouchStart(); track('CHARGE_BUTTON_HELD'); }}
                onTouchEnd={() => { handleTouchEnd(); track('CHARGE_BUTTON_RELEASED'); }}
                onTouchCancel={() => { handleTouchCancel(); }}
                onMouseLeave={() => { handleMouseLeave(); }}
              >
                {buttonDisabled ? t('gameUI.cooling') : t('gameUI.charge')}
              </button>
            </div>
          </div>
        </Html>
      )}
    </>
  )
}